import React from "react";
import { UserRound } from "lucide-react";
import { useCurrentUser } from "../../backend/queries/auth";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { ChatEmailPreference } from "./ChatEmailPreference";
import { Label } from "../../components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

const ProfilePage = () => {
  const { data, error, isPending: loading } = useCurrentUser();
  const user = data?.profile || data || null;

  return (
    <div className="flex flex-col">
      <div className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-5xl flex-col gap-5">
          <div>
            <h1 className="text-2xl font-semibold">Profile</h1>
            <p className="text-sm text-muted-foreground">
              View your portal identity and contact details.
            </p>
          </div>

          {loading ? (
            <StateCard text="Loading profile..." />
          ) : error ? (
            <StateCard tone="error" text={error.message} />
          ) : !user ? (
            <StateCard text="No user data found." />
          ) : (
            <Tabs defaultValue="profile">
              <TabsList>
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="pets">Pets</TabsTrigger>
                <TabsTrigger value="family">Family</TabsTrigger>
              </TabsList>

              <TabsContent value="profile" className="mt-5">
                <Card>
                  <CardHeader className="p-4 sm:p-5">
                    <CardTitle className="flex items-center gap-2">
                      <UserRound className="h-5 w-5" />
                      Profile information
                    </CardTitle>
                    <CardDescription>
                      Read-only details from your portal account.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid gap-4 p-4 pt-0 sm:grid-cols-2 sm:p-5 sm:pt-0">
                    <ReadOnlyField label="First name" value={user.firstname} />
                    <ReadOnlyField label="Last name" value={user.lastname} />
                    <ReadOnlyField
                      label="Email"
                      value={user.email}
                      type="email"
                    />
                    <ReadOnlyField label="Role" value={user.role} />
                    <ReadOnlyField
                      label="Contact number"
                      value={user.contactnumber}
                    />
                    <ReadOnlyField
                      label="Time zone"
                      value={user.ianatimezonecode || user.timezone}
                    />
                    <ReadOnlyField label="Language" value={user.language} />
                    <ReadOnlyField label="Country" value={user.country} />
                  </CardContent>
                </Card>
                <div className="mt-5">
                  <ChatEmailPreference />
                </div>
              </TabsContent>

              <TabsContent value="pets" className="mt-5">
                <StateCard text="Pets information is under construction." />
              </TabsContent>

              <TabsContent value="family" className="mt-5">
                <StateCard text="Family information is under construction." />
              </TabsContent>
            </Tabs>
          )}
        </div>
      </div>
    </div>
  );
};

const ReadOnlyField = ({ label, value, type = "text" }) => {
  const id = label.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} value={value || "N/A"} readOnly />
    </div>
  );
};

const StateCard = ({ text, tone }: { text: string; tone?: "error" }) => (
  <Card>
    <CardContent
      className={`p-8 text-center text-sm ${
        tone === "error" ? "text-destructive" : "text-muted-foreground"
      }`}
    >
      {text}
    </CardContent>
  </Card>
);

export default ProfilePage;
